import { type MouseEvent, useRef, useState } from "react";

import { Controller, type UseFormReturn } from "react-hook-form";
import PhoneInput from "react-phone-input-2";

import { classNames, Input, Select, Tooltip } from "@alpacahq/ui/old";

import { SectionDropdown } from "~/v2/components/common/SectionDropdown";
import "react-phone-input-2/lib/style.css";

import { InfoIcon } from "@phosphor-icons/react";
import type { Suggestion } from "use-places-autocomplete";

import { useOutsideClick } from "~/v2/api/hooks/useOutsideClick";
import { CountryCombobox } from "~/v2/components/form";
import type { Country } from "~/v2/types";
import { parseAddress } from "~/v2/utils";
import { FixedBottomMenu } from "../FixedBottomMenu";
import { type AdditionalInfoSchema, EMPLOYMENT_STATUS_OPTIONS } from "./schema";

export interface AdditionalInfoPresentationProps {
  form: UseFormReturn<AdditionalInfoSchema>;
  onBack: () => void;
  onSubmit: ReturnType<UseFormReturn<AdditionalInfoSchema>["handleSubmit"]>;
  countries: Country[];
  isEmployerInfoRequired: boolean;
  addressSuggestions: {
    isDisplayed: boolean;
    value: string;
    suggestions: Suggestion[];
    setValue: (suggestion: Suggestion) => void;
    onFocus: () => void;
    onClose: () => void;
  };
  isLoading: boolean;
  isGivenNameDisabled: boolean;
  isMiddleNameDisabled: boolean;
  isFamilyNameDisabled: boolean;
}

export const AdditionalInfoPresentation = ({
  form,
  isEmployerInfoRequired,
  countries,
  onBack,
  onSubmit,
  addressSuggestions,
  isLoading,
  isGivenNameDisabled,
  isMiddleNameDisabled,
  isFamilyNameDisabled,
}: AdditionalInfoPresentationProps) => {
  const [dropdownStates, setDropdownStates] = useState({
    personalInfo: true,
    employmentInfo: true,
  });

  const onDropdownToggled =
    (section: "personalInfo" | "employmentInfo") =>
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setDropdownStates((prev) => ({ ...prev, [section]: !prev[section] }));
    };

  const addressRef = useRef<HTMLDivElement>(null);
  useOutsideClick(addressRef, () => addressSuggestions.onClose());

  const {
    setValue,
    control,
    formState: { errors },
  } = form;

  return (
    <form onSubmit={onSubmit} className="flex h-full min-h-full flex-col gap-6">
      {/* Personal Information Section */}
      <SectionDropdown
        title="Personal Information"
        isOpen={dropdownStates.personalInfo}
        onToggleOpen={onDropdownToggled("personalInfo")}
      >
        <div className="flex flex-col gap-4">
          {/* First Name, Middle Name, Last Name */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
            <Controller
              name="given_name"
              control={control}
              rules={{ required: "First name is required" }}
              render={({ field }) => (
                <Input
                  {...field}
                  label="First Name"
                  disabled={isGivenNameDisabled}
                />
              )}
            />
            <Controller
              name="middle_name"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Middle Name"
                  disabled={isMiddleNameDisabled}
                />
              )}
            />
            <Controller
              name="family_name"
              control={control}
              rules={{ required: "Last name is required" }}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Last Name"
                  disabled={isFamilyNameDisabled}
                />
              )}
            />
          </div>

          {/* Country of Tax Residence */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
            <Controller
              name="country_of_tax_residence"
              control={control}
              rules={{ required: "Country of tax residence is required" }}
              render={({ field }) => (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-start gap-2">
                    <label
                      htmlFor={"country_of_tax_residence"}
                      className="font-semibold text-gray-700 text-sm"
                    >
                      Country of Tax Residence
                    </label>
                    <Tooltip content="Please select a country from the list">
                      <InfoIcon />
                    </Tooltip>
                  </div>
                  <CountryCombobox
                    name="country_of_tax_residence"
                    items={countries}
                    value={field.value}
                    onChange={field.onChange}
                    // Fires on deselection too, so an address cannot outlive the
                    // country it was entered for.
                    onDidSelect={() => {
                      setValue("street_address", "");
                      setValue("city", "");
                      setValue("state", "");
                      setValue("postal_code", "");
                    }}
                    placeholder="Select a country"
                    // Border only — the error text below keeps its `truncate`,
                    // which the Combobox's own message row lacks.
                    status={
                      errors.country_of_tax_residence ? "error" : "neutral"
                    }
                  />
                  {errors.country_of_tax_residence && (
                    <p className="truncate text-rose-500 text-xs">
                      {errors.country_of_tax_residence?.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>

          <div className="grid grid-cols-1 xs:grid-cols-2 gap-x-6 gap-y-4">
            {/* Street Address and City */}
            <Controller
              name="street_address"
              control={control}
              rules={{
                required: "Street address is required",
              }}
              render={({ field: { ref, ...field } }) => (
                <div
                  className="relative"
                  ref={addressRef}
                  onFocus={addressSuggestions.onFocus}
                >
                  <Input
                    {...field}
                    type="text"
                    label="Street Address"
                    placeholder="Search or enter"
                    status={errors.street_address ? "error" : "neutral"}
                    errorMsg={errors.street_address?.message}
                    onChange={(event) => {
                      field.onChange(event);
                      addressSuggestions.setValue(event.target.value);
                    }}
                  />
                  {addressSuggestions.isDisplayed && (
                    <ul className="absolute top-16 left-0 z-10 flex w-full flex-col gap-1 rounded border border-gray-400 bg-white p-2">
                      {addressSuggestions.suggestions.map((suggestion) => (
                        <li
                          key={suggestion.description}
                          onClick={async () => {
                            field.onChange(
                              suggestion.structured_formatting.main_text,
                            );

                            try {
                              const address = await parseAddress(
                                suggestion.description,
                              );
                              setValue("city", address.city ?? "");
                              setValue("state", address.state ?? "");
                              setValue(
                                "postal_code",
                                address.postal_code ?? "",
                              );
                            } catch (error) {
                              console.error(
                                "error parsing autocomplete address",
                                error,
                              );
                            }

                            addressSuggestions.setValue(suggestion);
                            addressSuggestions.onClose();
                          }}
                          className="cursor-pointer rounded bg-white p-1.5 font-medium text-gray-700 text-sm transition-colors hover:bg-gray-50"
                        >
                          {suggestion.description}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            />

            <Controller
              name="city"
              control={control}
              rules={{ required: "City is required" }}
              render={({ field }) => (
                <Input
                  {...field}
                  label="City"
                  placeholder="Enter city"
                  status={errors.city ? "error" : "neutral"}
                  errorMsg={errors.city?.message}
                />
              )}
            />

            {/* State/Province, Zip Code */}
            <Controller
              name="state"
              control={control}
              rules={{ required: "State/Province is required" }}
              render={({ field }) => (
                <Input
                  {...field}
                  label="State / Province"
                  placeholder="Enter State/Province"
                  status={errors.state ? "error" : "neutral"}
                  errorMsg={errors.state?.message}
                />
              )}
            />
            <Controller
              name="postal_code"
              control={control}
              rules={{ required: "Zip code is required" }}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Zip Code"
                  placeholder="Enter zip code"
                  status={errors.postal_code ? "error" : "neutral"}
                  errorMsg={errors.postal_code?.message}
                />
              )}
            />

            {/* Unit/Apt and Phone Number */}
            <Controller
              name="unit"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Unit / Apt #"
                  placeholder="Enter unit"
                  status={errors.unit ? "error" : "neutral"}
                  errorMsg={errors.unit?.message}
                />
              )}
            />
            <div className="flex flex-col gap-1">
              <label className="flex items-center justify-start gap-1 font-semibold text-gray-700 text-sm">
                Phone Number
              </label>
              <Controller
                name="phone_number"
                control={control}
                rules={{ required: "Phone number is required" }}
                render={({ field: { onChange, value } }) => (
                  <PhoneInput
                    country="us"
                    value={value}
                    onChange={onChange}
                    inputClass="block w-full h-9 rounded border bg-white text-sm font-medium transition-colors focus:ring-0 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-300 disabled:placeholder:text-gray-300 focus:border-gray-500 border-gray-400 placeholder-shown:border-gray-400 text-gray-700 placeholder-gray-400"
                    containerClass="w-full"
                    placeholder="+1 (123) 456-7890"
                  />
                )}
              />
              {errors.phone_number && (
                <p className="m-0 p-0 text-red-500 text-sm">
                  {errors.phone_number.message}
                </p>
              )}
            </div>
          </div>
        </div>
      </SectionDropdown>

      {/* Employment Information */}
      <SectionDropdown
        title="Employment information"
        isOpen={dropdownStates.employmentInfo}
        onToggleOpen={onDropdownToggled("employmentInfo")}
      >
        <div className="flex flex-col gap-4">
          {/* Employment Status */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
            <Controller
              name="employment_status"
              control={control}
              rules={{ required: "Employment status is required" }}
              render={({ field: { ref: _ref, ...field } }) => (
                <Select
                  {...field}
                  label="Status"
                  options={EMPLOYMENT_STATUS_OPTIONS}
                  onChange={(value) => {
                    field.onChange(value);
                    if (value !== "EMPLOYED") {
                      setValue("employer", "");
                      setValue("employer_address", "");
                      setValue("position", "");
                      setValue("function", "");
                    }
                  }}
                  placeholder="Select employment status"
                  errorMsg={errors.employment_status?.message}
                />
              )}
            />
          </div>

          {/* Employer Info - Only show if employed */}
          <div
            className={classNames(
              "grid max-h-0 grid-cols-1 xs:grid-cols-2 gap-x-6 gap-y-4 overflow-hidden transition-all duration-300 ease-in-out",
              isEmployerInfoRequired && "max-h-[1000px]",
            )}
          >
            {/** Company name, address */}
            <Controller
              name="employer"
              control={control}
              rules={{
                required: isEmployerInfoRequired,
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Company Name"
                  placeholder="Enter name"
                  status={errors.employer ? "error" : "neutral"}
                  errorMsg={errors.employer?.message}
                />
              )}
            />
            <Controller
              name="employer_address"
              control={control}
              rules={{
                required: isEmployerInfoRequired,
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Company Address"
                  placeholder="Enter address"
                  status={errors.employer_address ? "error" : "neutral"}
                  errorMsg={errors.employer_address?.message}
                />
              )}
            />
            {/** Position and Function */}
            <Controller
              name="position"
              control={control}
              rules={{
                required: isEmployerInfoRequired,
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Job Title"
                  placeholder="Enter title"
                  status={errors.position ? "error" : "neutral"}
                  errorMsg={errors.position?.message}
                />
              )}
            />
            <Controller
              name="function"
              control={control}
              rules={{
                required: isEmployerInfoRequired,
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Job Function"
                  placeholder="Enter function"
                  status={errors.function ? "error" : "neutral"}
                  errorMsg={errors.function?.message}
                />
              )}
            />
          </div>
        </div>
      </SectionDropdown>

      <FixedBottomMenu
        className="mt-auto self-end"
        back={{
          onClick: onBack,
          disabled: isLoading,
        }}
        next={{
          type: "submit",
          label: "Continue",
          onClick: onSubmit,
          loading: isLoading,
          disabled: isLoading,
        }}
      />
    </form>
  );
};
